// apps/codeframe/src/chrome/timeline/useMotionScope.ts
import { useCanvasStore } from '../../store/canvas-store';
import { getPlayer } from '../../motion/registry';
import type { Frame, MotionTimeline, MotionProp } from '../../store/types';

// Design frames that can carry a timeline. TSX frames are included now that
// TsxRenderer stamps data-cf-id onto the rendered React tree (stampDomCfIds),
// so the sampler + node picker can address their nodes.
const DESIGN_KINDS = new Set<Frame['kind']>(['free', 'main', 'tsxFree', 'tsxMain']);

function motionOf(f: Frame | undefined): MotionTimeline | undefined {
  return f && 'motion' in f ? (f as { motion?: MotionTimeline }).motion : undefined;
}

export interface MotionScope {
  frame: Frame;
  frameId: string;
  /** Timeline if present (undefined → frame has no motion yet). */
  timeline: MotionTimeline | undefined;
  /** True when this frame kind can be animated on the timeline (free/main/tsxFree/tsxMain). */
  canAnimate: boolean;
}

/** Resolve the single selected design frame for the timeline dock, or null. */
export function useMotionScope(): MotionScope | null {
  const frames = useCanvasStore((s) => s.doc.frames);
  const selection = useCanvasStore((s) => s.selection);
  if (selection.length !== 1) return null;
  const frame = frames.find((f) => f.id === selection[0]);
  if (!frame) return null;
  return {
    frame,
    frameId: frame.id,
    timeline: motionOf(frame),
    canAnimate: DESIGN_KINDS.has(frame.kind),
  };
}

/** Live player for the scoped frame (registered by useFrameMotion). */
export function usePlayerFor(frameId: string | undefined) {
  return frameId ? getPlayer(frameId) : undefined;
}

export const ALL_PROPS: MotionProp[] = [
  'x', 'y', 'rotate', 'scale', 'scaleX', 'scaleY', 'opacity', 'color', 'backgroundColor', 'blur',
];
